import { useCallback, useEffect, useMemo, useState } from "react";
import { API, formatApiErrorDetail } from "../auth/api";

/**
 * Fetch the TPA × State coverage matrix.
 *   region:      optional region key (e.g. "southeast"); omit for all regions.
 *
 * Returns { regions, region, states, tpas, rows, coverageByState, loading, error }.
 *   rows[]           : { region, state, tpa, carriers: [], notes }
 *   coverageByState  : {
 *     [stateCode]: {
 *       tpas: [{ tpa, carriers[] }],
 *       tpaCount: <int>,             // TPAs with ≥1 carrier here
 *       carriers: Set<string>,       // union of unique carriers here
 *       carrierCount: <int>,
 *     }
 *   }
 */
export function useTpaNetworks(region) {
  const [data, setData] = useState({
    regions: [],
    region: region || null,
    states: [],
    tpas: [],
    rows: [],
    state_market_tiers: {},
    market_tiers_disclaimer: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = region ? { region } : {};
      const { data: payload } = await API.get("/tpa-networks", { params });
      setData(payload);
      setError(null);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [region]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const coverageByState = useMemo(() => {
    const out = {};
    for (const r of data.rows || []) {
      if (!r.carriers || r.carriers.length === 0) continue;
      if (!out[r.state]) {
        out[r.state] = { tpas: [], tpaCount: 0, carriers: new Set(), carrierCount: 0 };
      }
      out[r.state].tpas.push({ tpa: r.tpa, carriers: r.carriers });
      out[r.state].tpaCount += 1;
      for (const c of r.carriers) out[r.state].carriers.add(c);
    }
    for (const s of Object.keys(out)) {
      out[s].carrierCount = out[s].carriers.size;
    }
    return out;
  }, [data.rows]);

  return { ...data, coverageByState, loading, error, refetch };
}
